// /share/$token — public review-link viewer (PRD §9.5 +
// docs/frontend_architecture.md §6).
//
// Public route — `__root.tsx`'s `beforeLoad` whitelists the `/share/`
// prefix, so this surface renders without an auth bootstrap. The
// component holds the access payload in local state once <ShareGate>
// resolves. The player landed in B2c; comments in B2d; annotations
// in B2e; playlist strip in B2f; responsive layout + theme + empty-
// state polish in B2g (this slice).
//
// Layout (B2g):
//   - <768px: the candidate panel (video + canvas) stacks on top of
//     the comment thread.
//   - ≥768px: panel + comments render side-by-side, with the comment
//     column fixed at w-80 and the panel taking the remaining space.
// The swap is driven by `useMediaQuery('(min-width: 768px)')` so the
// behavior is testable from JS — Tailwind responsive utilities would
// work too but vitest can't introspect them.
//
// Theme: the share viewer participates in the same themeStore as the
// authed shell. We mount a <ThemeToggle> in the header so anonymous
// viewers can override system preference without needing an account.
// The viewer is unauthed by definition; `themeStore` is auth-agnostic
// and reads from localStorage on init, so this Just Works.
//
// Frame state lives at the CandidatePanel layer: VideoPlayer reports
// currentTime via `onTimeChange`, and the panel converts that to an
// integer frame for the AnnotationCanvas query key. fps is unknown
// today (CandidateOut doesn't expose it yet) so we fall back to
// `Math.floor(currentTime)` — a second-resolution "frame" key that
// still partitions annotations meaningfully along the timeline.

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { apiErrorMessage } from '../api/client';
import type { ShareAccessResponse } from '../api/mutations/share';
import { useShareAssetUrl, useShareCandidate } from '../api/queries/share';
import type { components } from '../api/schema';
import { AnnotationCanvas } from '../components/AnnotationCanvas';
import { CommentThread } from '../components/CommentThread';
import { PanelWithComments } from '../components/PanelWithComments';
import { PlaylistStrip } from '../components/PlaylistStrip';
import { ShareGate } from '../components/ShareGate';
import { Skeleton } from '../components/Skeleton';
import { ThemeToggle } from '../components/ThemeToggle';
import { VideoPlayer } from '../components/VideoPlayer';

type AssetOut = components['schemas']['AssetOut'];
type CandidateOut = components['schemas']['CandidateOut'];
type PlaylistOut = components['schemas']['PlaylistOut'];

export const Route = createFileRoute('/share/$token')({
  component: ShareRoute,
});

function ShareRoute(): React.JSX.Element {
  const { token } = Route.useParams();
  const [access, setAccess] = useState<ShareAccessResponse | null>(null);

  if (access === null) {
    return <ShareGate token={token} onAccess={setAccess} />;
  }
  return <ShareViewer token={token} access={access} />;
}

/**
 * Pick the most playable video asset from a candidate. Order matters:
 * the H.264 proxy is broadly browser-compatible and bandwidth-friendly,
 * the source is the fallback when no proxy exists. Stills (mime
 * `image/*`) deliberately don't match — the still-image surface is its
 * own affordance and lands in a later slice.
 */
function pickPlayableAsset(assets: AssetOut[]): AssetOut | null {
  const proxy = assets.find((a) => a.kind === 'proxy_h264');
  if (proxy !== undefined) return proxy;
  const source = assets.find(
    (a) => a.kind === 'source' && a.mime_type.startsWith('video/')
  );
  return source ?? null;
}

interface ShareViewerProps {
  token: string;
  access: ShareAccessResponse;
}

export function ShareViewer({
  token,
  access,
}: ShareViewerProps): React.JSX.Element {
  const { link, scope, candidate, playlist } = access;
  // Distinguish "valid link, nothing attached" (the link exists but its
  // candidate/playlist row is gone or never set) from the populated
  // cases. The share_service shouldn't actually emit this in v1, but a
  // dangling row would otherwise render as a silent blank — surface it
  // with a real alert so the viewer knows what's wrong.
  const hasContent =
    (scope === 'candidate' && candidate !== null && candidate !== undefined) ||
    (scope === 'playlist' && playlist !== null && playlist !== undefined);

  return (
    <div className="mx-auto max-w-screen-xl p-7">
      <header className="mb-7 flex items-start justify-between gap-4">
        <div>
          <p className="text-text-faint text-11 mb-2 uppercase tracking-wider">
            KansiTRK · share
          </p>
          <h1 className="text-text text-28 font-semibold">
            {scope === 'candidate'
              ? 'Candidate review'
              : (playlist?.name ?? 'Playlist review')}
          </h1>
          {link.watermark_text !== null && (
            <p className="text-text-muted mt-2 text-13">
              Watermark:{' '}
              <span className="font-mono">{link.watermark_text}</span>
            </p>
          )}
        </div>
        <ThemeToggle />
      </header>

      {scope === 'candidate' && candidate !== null && candidate !== undefined && (
        <CandidateScopeView
          token={token}
          candidate={candidate}
          canDraw={link.can_comment}
          canComment={link.can_comment}
          canCommentAnonymously={link.can_comment_anonymously}
        />
      )}
      {scope === 'playlist' && playlist !== null && playlist !== undefined && (
        <PlaylistView
          token={token}
          playlist={playlist}
          canDraw={link.can_comment}
          canComment={link.can_comment}
          canCommentAnonymously={link.can_comment_anonymously}
        />
      )}
      {!hasContent && (
        <section
          role="alert"
          aria-label="Empty share link"
          className="border-border bg-bg-elevated/60 rounded-2 border border-dashed px-6 py-7"
        >
          <h2 className="text-text text-15 font-semibold">Nothing to show</h2>
          <p className="text-text-muted mt-1 text-13">
            This share link is valid but no candidate or playlist is attached.
            The link owner may have removed the content — ask them to refresh
            the link.
          </p>
        </section>
      )}

      <footer className="mt-7 flex flex-wrap gap-2">
        {link.can_comment && (
          <span className="border-border text-text-muted rounded-2 border px-2 py-1 text-11">
            Comments enabled
          </span>
        )}
        {link.can_comment_anonymously && (
          <span className="border-border text-text-muted rounded-2 border px-2 py-1 text-11">
            Anonymous comments
          </span>
        )}
        {link.can_download && (
          <span className="border-border text-text-muted rounded-2 border px-2 py-1 text-11">
            Downloads enabled
          </span>
        )}
      </footer>
    </div>
  );
}

interface CandidateScopeViewProps {
  token: string;
  candidate: CandidateOut;
  canDraw: boolean;
  canComment: boolean;
  canCommentAnonymously: boolean;
}

function CandidateScopeView({
  token,
  candidate,
  canDraw,
  canComment,
  canCommentAnonymously,
}: CandidateScopeViewProps): React.JSX.Element {
  return (
    <PanelWithComments
      panel={
        <CandidatePanel
          token={token}
          candidate={candidate}
          canDraw={canDraw}
        />
      }
      comments={
        <CommentThread
          mode="share"
          token={token}
          candidateId={candidate.id}
          canComment={canComment}
          canCommentAnonymously={canCommentAnonymously}
        />
      }
    />
  );
}

interface CandidatePanelProps {
  token: string;
  candidate: CandidateOut;
  canDraw: boolean;
}

function CandidatePanel({
  token,
  candidate,
  canDraw,
}: CandidatePanelProps): React.JSX.Element {
  const playable = pickPlayableAsset(candidate.assets ?? []);
  const url = useShareAssetUrl(token, candidate.id, playable?.id);
  // Wall-clock playhead; the canvas converts to an integer frame key.
  const [currentTime, setCurrentTime] = useState<number>(0);
  const frame = Math.max(0, Math.floor(currentTime));

  if (playable === null) {
    return (
      <section
        aria-label="Candidate"
        className="border-border bg-bg-elevated/60 rounded-2 border border-dashed px-6 py-7"
      >
        <p className="text-text text-13">
          This candidate has no playable video asset yet.
        </p>
      </section>
    );
  }

  return (
    <section aria-label="Candidate" className="flex flex-col gap-3">
      {url.isPending && (
        <Skeleton className="aspect-video w-full" />
      )}
      {url.isError && (
        <div
          role="alert"
          className="border-border bg-bg-elevated/60 rounded-2 border border-dashed px-6 py-7"
        >
          <p className="text-text text-13">
            {apiErrorMessage(url.error, 'Could not load this video.')}
          </p>
        </div>
      )}
      {url.data !== undefined && (
        // The video + canvas share the same column slot; the canvas's
        // own wrapper handles the absolute overlay positioning.
        <div className="relative flex flex-col gap-2">
          <VideoPlayer
            src={url.data.url}
            mimeType={url.data.mime_type}
            onTimeChange={setCurrentTime}
          />
          <AnnotationCanvas
            mode="share"
            token={token}
            candidateId={candidate.id}
            frame={frame}
            canDraw={canDraw}
          />
        </div>
      )}
    </section>
  );
}

interface PlaylistViewProps {
  token: string;
  playlist: PlaylistOut;
  canDraw: boolean;
  canComment: boolean;
  canCommentAnonymously: boolean;
}

/**
 * Playlist-scoped viewer (B2f). Owns the `activeCandidateId` state,
 * fetches the active candidate's full payload via `useShareCandidate`,
 * and renders the same `<CandidatePanel>` + `<CommentThread>` stack
 * the candidate-scoped path uses — just driven by a swappable id
 * rather than a fixed one from the access response. The strip below
 * is the navigation surface; clicking a tile sets `activeCandidateId`
 * and the cache makes the swap effectively instant after the first
 * visit to each candidate.
 *
 * Responsive: the panel + comments use the same two-column shell as
 * the candidate-scoped view (PanelWithComments). The strip sits below
 * spanning the full width in both layouts — it's a horizontal scroller
 * and the natural place for it is under the active tile.
 */
function PlaylistView({
  token,
  playlist,
  canDraw,
  canComment,
  canCommentAnonymously,
}: PlaylistViewProps): React.JSX.Element {
  // OpenAPI marks `items` optional (Pydantic Field(default_factory=list)
  // — not required in the schema). Coalesce so the component can rely
  // on a real array everywhere downstream.
  const items = playlist.items ?? [];
  // Initial active = first item by position (the playlist service
  // returns items sorted, but defensive-pick anyway).
  const initial = items.length > 0 ? items[0]?.candidate_id ?? null : null;
  const [activeId, setActiveId] = useState<string | null>(initial);

  const active = useShareCandidate(token, activeId ?? undefined);

  if (initial === null) {
    return (
      <section
        aria-label="Playlist"
        className="border-border bg-bg-elevated/60 rounded-2 border border-dashed px-6 py-7"
      >
        <p className="text-text text-13">This playlist is empty.</p>
      </section>
    );
  }

  return (
    <section aria-label="Playlist" className="flex flex-col gap-6">
      {active.isPending && <Skeleton className="aspect-video w-full" />}
      {active.isError && (
        <div
          role="alert"
          className="border-border bg-bg-elevated/60 rounded-2 border border-dashed px-6 py-7"
        >
          <p className="text-text text-13">
            {apiErrorMessage(active.error, 'Could not load this candidate.')}
          </p>
        </div>
      )}
      {active.data !== undefined && (
        <PanelWithComments
          panel={
            <CandidatePanel
              token={token}
              candidate={active.data}
              canDraw={canDraw}
            />
          }
          comments={
            <CommentThread
              mode="share"
              token={token}
              candidateId={active.data.id}
              canComment={canComment}
              canCommentAnonymously={canCommentAnonymously}
            />
          }
        />
      )}
      <PlaylistStrip
        token={token}
        items={items}
        activeCandidateId={activeId ?? ''}
        onSelect={setActiveId}
      />
    </section>
  );
}
